import React, { useState } from 'react';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { LogIn } from 'lucide-react';

const Login = () => {
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        login(data.token, data.user);
        toast.success('Login successful');
      } else {
        toast.error(data.detail || 'Authentication failed');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Logo */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 relative">
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 flex flex-col items-center justify-center w-full p-12">
          <img 
            src="https://customer-assets.emergentagent.com/job_stockflow-566/artifacts/cesj488x_Vape-Avenue-PNG.png" 
            alt="Vape Avenue Logo" 
            className="w-80 h-80 object-contain mb-6"
          />
          <p className="font-mono text-muted-foreground text-sm uppercase tracking-widest">Inventory Management System</p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h2 className="font-heading font-bold text-3xl mb-2" data-testid="login-title">
              Sign In
            </h2>
            <p className="text-muted-foreground font-body">Enter your credentials to continue</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="mt-1.5"
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="mt-1.5"
                data-testid="password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full mt-6 bg-primary hover:bg-primary/90 transition-all active:scale-95"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              <LogIn size={18} strokeWidth={1.5} className="mr-2" />
              {loading ? 'Please wait...' : 'Sign In'}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Contact your administrator if you need an account
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
